import { useId } from 'react'
import { InputNumber, Slider } from 'antd'
import {
  ADVICEPREP_ENTRY_CONNECTIONS,
  ADVICEPREP_MAX_SLIDER_CONNECTIONS,
  advicePrepCommittedMonthly,
  calculateAdvicePrepPrice,
  estimateAdvicePrepUsage,
  formatAdvicePrepCurrency,
  normalizeConnections,
} from '../pricing'
import Tile from './Tile'

type ConnectionsEstimatorProps = {
  connections: number
  onChange: (connections: number) => void
  accent: string
  disabled?: boolean
}

export default function ConnectionsEstimator({
  connections,
  onChange,
  accent,
  disabled,
}: ConnectionsEstimatorProps) {
  // Per instance, not a module constant: the upsell and manage cards both render
  // this, and a shared id would break the label pairing if they ever co-existed.
  const inputId = useId()
  const safeConnections = normalizeConnections(connections)
  const monthly = calculateAdvicePrepPrice(safeConnections)
  const usage = estimateAdvicePrepUsage(safeConnections)

  return (
    <div>
      <InputNumber
        id={inputId}
        className="mb-3 w-24"
        min={1}
        max={10000}
        value={safeConnections}
        disabled={disabled}
        onChange={(value) => onChange(normalizeConnections(value))}
      />

      <div className="flex items-center gap-3">
        {/* The input sits on its own line above, so the association has to be explicit. */}
        <label htmlFor={inputId} className="shrink-0 text-sm font-medium text-gray-800">
          Client Xero connections
        </label>
        <Slider
          className="min-w-0 flex-1"
          min={1}
          max={ADVICEPREP_MAX_SLIDER_CONNECTIONS}
          value={Math.min(safeConnections, ADVICEPREP_MAX_SLIDER_CONNECTIONS)}
          onChange={(value) => onChange(normalizeConnections(value))}
          disabled={disabled}
          tooltip={{ open: false }}
          ariaLabelForHandle="Client Xero connections"
          styles={{ track: { background: accent } }}
        />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Tile
          label="Monthly"
          value={formatAdvicePrepCurrency(monthly)}
          caption={`estimated at ${safeConnections}`}
          accent={accent}
        />
        <Tile
          label="Per connection"
          value={formatAdvicePrepCurrency(monthly / safeConnections, 2)}
          caption={`estimated at ${safeConnections}`}
        />
      </div>

      <p className="mt-3 text-xs leading-5 text-gray-500">
        {formatAdvicePrepCurrency(advicePrepCommittedMonthly())} of that is the flat fee, covering your first{' '}
        {ADVICEPREP_ENTRY_CONNECTIONS} connections, and it is the only committed amount.
        {usage > 0
          ? ` The remaining ${formatAdvicePrepCurrency(usage)} is usage — measured from the organisations you actually connect and billed in arrears.`
          : ` Connections past ${ADVICEPREP_ENTRY_CONNECTIONS} are billed on usage, measured from the organisations you actually connect.`}
      </p>
    </div>
  )
}
